import { UnitHistory } from '../unit-specific-facts';

type AreaFacts = {
  [key: string]: UnitHistory;
};

export const areaFacts: AreaFacts = {
  "square meter": {
    name: "Square Meter",
    shortDescription: "The SI-derived unit of area",
    history: "The square meter embodies the elegant simplicity of the metric system. Its development marked a departure from traditional area measurements based on agricultural work units like the acre. Instead, it offered a purely geometric definition - the area of a square with sides one meter long. This seemingly simple concept revolutionized architecture and engineering by creating a universal standard independent of local customs.",
    origin: "Derived directly from the meter as part of the metric system's rational approach to measurement",
    modernUsage: "Modern laser measuring tools can determine square meters with millimeter precision, though traditional measuring techniques using calibrated tapes remain common in construction",
    funFacts: [
      "Ancient Roman architects used a similar concept with their pes quadratus (square foot), showing how geometric area measurements transcend cultures",
      "The first precision measurements of the square meter involved elaborate mechanisms using calibrated metal bars and optical instruments",
      "The official calibration of square meter measurements was crucial for the standardization of land registry across Europe"
    ],
    related: ["acre", "hectare"],
    images: []
  },
  acre: {
    name: "Acre",
    shortDescription: "A unit of land area",
    history: "The acre originated as the area a yoke of oxen could plow in one day. Its standardization to 43,560 square feet (4,840 square yards) preserved its relationship to traditional units like chains and furlongs. The unusual number comes from its original definition as an area one chain wide by one furlong long.",
    origin: "Derived from medieval agricultural practices",
    modernUsage: "Surveyors historically used chains and poles for acre measurement, with modern GPS systems now providing precise digital alternatives",
    funFacts: [
      "Equal to 43,560 square feet",
      "Originally based on a day's plowing",
      "Defined as one chain by one furlong"
    ],
    related: ["square meter", "hectare"],
    images: []
  },
  hectare: {
    name: "Hectare",
    shortDescription: "A metric unit of area equal to 10,000 square meters",
    history: "The hectare (10,000 square meters) was designed for agricultural use, being roughly equivalent to traditional European field units like the acre. The name combines 'hecto' (hundred) with 'are' (100 square meters), reflecting the metric system's decimal nature.",
    origin: "Part of the metric system",
    modernUsage: "Its size made it particularly useful for land registry and farming, bridging the gap between traditional agricultural units and metric measurement",
    funFacts: [
      "Equal to 10,000 square meters",
      "Name combines 'hecto' and 'are'",
      "Approximately 2.47 acres"
    ],
    related: ["square meter", "acre"],
    images: []
  },
  "square kilometer": {
    name: "Square Kilometer",
    shortDescription: "A metric unit of area equal to one million square meters",
    history: "The square kilometer demonstrates the metric system's scalability, being exactly one million square meters. While too large for most everyday measurements, it became crucial for geography, urban planning, and national land management.",
    origin: "Derived from the kilometer unit of the metric system",
    modernUsage: "Early square kilometer measurements relied on triangulation surveys, while modern satellite systems provide instant square kilometer calculations",
    funFacts: [
      "Equal to one million square meters",
      "Used primarily in geography and planning",
      "Modern measurements use satellite systems"
    ],
    related: ["square meter", "square mile"],
    images: []
  },
  "square mile": {
    name: "Square Mile",
    shortDescription: "A unit of area equal to 640 acres",
    history: "The square mile (640 acres) became particularly important in American land management through the Public Land Survey System, which divided territory into one-square-mile sections. The unit played a crucial role in American westward expansion, with township plats based on six-by-six mile squares.",
    origin: "Traditional English measurement unit",
    modernUsage: "Early surveyors used chains and compasses to lay out square miles, facing challenges from rough terrain and magnetic variation",
    funFacts: [
      "Equal to 640 acres",
      "Used in American Public Land Survey System",
      "Township plats were six by six miles"
    ],
    related: ["acre", "square kilometer"],
    images: []
  },
  barn: {
    name: "Barn",
    shortDescription: "A unit of area used in nuclear physics",
    history: "The barn stands unique among area measurements as the only one named by nuclear physicists. Defined as 10⁻²⁸ square meters, it represents the typical cross-sectional area of an atomic nucleus. The name came from the humorous observation that this incredibly tiny area was 'as big as a barn' compared to the even smaller areas they were studying.",
    origin: "Named by nuclear physicists during the Manhattan Project",
    modernUsage: "This unit demonstrates how specialized fields develop their own measurement systems, even with tongue-in-cheek names",
    funFacts: [
      "Equal to 10⁻²⁸ square meters",
      "Named as a physics joke",
      "Used for atomic cross-sections"
    ],
    related: ["square meter"],
    images: []
  },
  morgen: {
    name: "Morgen",
    shortDescription: "A historical unit of land area based on morning work",
    history: "The morgen (meaning 'morning' in German and Dutch) was defined as the amount of land that could be plowed by one man behind one ox in the morning hours of a day. This practical unit varied by region and terrain, ranging from 0.6 to 2.5 acres. The unit survived into modern times in South Africa, where it became standardized at about 2.1 acres.",
    origin: "From German and Dutch word for 'morning'",
    modernUsage: "The morning-based measurement reflects the careful attention pre-industrial farmers paid to optimal working conditions",
    funFacts: [
      "Varied by region from 0.6 to 2.5 acres",
      "Standardized in South Africa at 2.1 acres",
      "Based on morning plowing capacity"
    ],
    related: ["acre", "hectare"],
    images: []
  },
  hide: {
    name: "Hide",
    shortDescription: "An Anglo-Saxon unit of land measurement",
    history: "The hide, an Anglo-Saxon unit of land measurement, represents one of history's most socially-oriented measuring systems. Originally defined as the amount of land sufficient to support one free family and its dependents (about 120 acres), the hide became crucial for tax assessment in medieval England.",
    origin: "Anglo-Saxon measurement system",
    modernUsage: "The actual size varied significantly based on soil quality and local customs - poorer soil meant larger hides, as more land was needed to support a family",
    funFacts: [
      "Approximately 120 acres",
      "Used for medieval tax assessment",
      "Size varied based on soil quality"
    ],
    related: ["acre", "morgen"],
    images: []
  }
};
